import {Device} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/config.types';
import {
  CouponInfoSheet,
  Coupon_BrandAndCategory,
  Coupon_DiscountValue,
  Coupon_HighTierCoupon_ProductsInformation,
  Coupon_HotDealCoupon_ProductAndBrandInformation,
  Coupon_SimpleCoupon,
  Coupon_Tier,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useTranslation} from '@aboutyou/i18n';
import {useHistory} from '@aboutyou/router';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  Flyout,
  GenericIconName,
  Icon,
  Text,
  mediaMinXXXS,
} from '@aboutyou/ui-components';
import React, {FC, useRef} from 'react';
import {useRect, useViewportEffect} from 'react-viewport-utils';
import styled, {css} from 'styled-components';
import {CouponFooter} from '../../../features/CouponCard/components/CouponFooter';
import {CouponTiers} from '../../../features/CouponCard/components/CouponTiers';
import {CouponAppOnlyBadge} from '../../../features/Coupons/CouponBadges/CouponBadges';
import {
  CouponExpiryDateCountdown,
  StyledCouponExpiryDateCountdownIcon,
  StyledCouponExpiryDateCountdownUnitComponent,
} from '../../../features/Coupons/CouponExpiryDateCountdown/CouponExpiryDateCountdown';
import {CouponInfoContentPopper} from '../../../features/Coupons/CouponInfoContentPopper/CouponInfoContentPopper';
import {useCouponInfoContentPopperStateManager} from '../../../features/Coupons/CouponInfoContentPopper/useCouponInfoContentPopperStateManager';
import {useIsUrgencyTriggersDisabled} from '../../../shared-deprecated/useIsUrgencyTriggersDisabled';

interface BaseProps {
  couponPromotionId: string;
  headline: string;
  expiryDate?: Date;
  info?: CouponInfoSheet;
  discountValue?: Coupon_DiscountValue;
  brandAndCategory?: Coupon_BrandAndCategory;
  redeemableDevices: Device[];
  label?: string;
  productAndBrandInformation?: Coupon_HotDealCoupon_ProductAndBrandInformation;
  tiers?: Coupon_Tier[];
  discountCondition?: string;
  productsInformation?: Coupon_HighTierCoupon_ProductsInformation;
  isAppOnlyCoupon?: boolean;
}

interface CouponHeaderBaseProps extends BaseProps {
  ['data-testid']?: string;
}

interface CouponBodyWithDiscountValueProps {
  dataTestId?: string;
  discountValue: Coupon_SimpleCoupon['discountValue'];
  discountCondition?: string;
  headline: string;
}

const HEADLINE_LINE_HEIGHT = 18;

const CouponBodyWithDiscountValue = ({
  dataTestId,
  discountValue,
  headline,
  discountCondition,
  ref,
}: CouponBodyWithDiscountValueProps & {ref?: React.Ref<HTMLDivElement>}) => {
  const headlineRef = useRef<HTMLSpanElement>(null);
  const {height: headlineHeight} = useRect(headlineRef, {
    disableScrollUpdates: true,
  }) || {height: 0};
  const isHeadlineMoreThanThreeLines =
    headlineHeight > HEADLINE_LINE_HEIGHT * 3;

  return (
    <DiscountValueWrapper ref={ref}>
      <DiscountValuePart>
        <DiscountValue
          size="px20"
          color="inherit"
          data-testid={`${dataTestId}DiscountValue`}
        >
          {discountValue.value}
          {discountValue.unit}
        </DiscountValue>
        {discountCondition && (
          <DiscountConditionText size="px11" color="PrimaryCoupon">
            {discountCondition}
          </DiscountConditionText>
        )}
      </DiscountValuePart>

      <Headline
        size="px14"
        weight="mediumHeavy"
        color="inherit"
        data-testid={`${dataTestId}Headline`}
        ref={headlineRef}
        $isHeadlineMoreThanThreeLines={isHeadlineMoreThanThreeLines}
      >
        {headline}
      </Headline>
    </DiscountValueWrapper>
  );
};

const CouponBodyWithTiers = ({
  tiers,
  ref,
}: {
  tiers: Coupon_Tier[];
  ref?: React.Ref<HTMLDivElement>;
}) => {
  return (
    <TierWrapper ref={ref}>
      <StyledCouponTiers
        tiers={tiers}
        mode="regular"
        forceActiveState
        $tiersNumber={tiers.length}
      />
    </TierWrapper>
  );
};

export const CouponLandingPageHeaderBaseMobile: FC<
  CouponHeaderBaseProps
> = props => {
  const disableUrgencyTriggers = useIsUrgencyTriggersDisabled();
  const {t} = useTranslation();

  const {
    expiryDate,
    info,
    'data-testid': dataTestId,
    headline,
    discountValue,
    brandAndCategory,
    productAndBrandInformation,
    tiers,
    discountCondition,
    productsInformation,
    isAppOnlyCoupon,
  } = props;

  const history = useHistory();
  const {open} = useCouponInfoContentPopperStateManager();
  const [isScrolled, setIsScrolled] = useHistoryPageState(false, {
    uniqueKey: 'isScrolled',
  });
  const bodyRef = useRef(null);
  const footerRef = useRef(null);

  const {height: bodyHeight} = useRect(
    bodyRef,
    {
      disableScrollUpdates: true,
      priority: 'low',
    },
    [headline],
  ) || {height: 0};

  const {height: footerHeight} = useRect(
    footerRef,
    {
      disableScrollUpdates: true,
      priority: 'low',
    },
    [headline],
  ) || {height: 0};

  const onInfoIconClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    open();
  };

  const onBackClick = () => {
    history.goBack();
  };

  useViewportEffect(
    ({scroll}) => {
      setIsScrolled(scroll.y >= 170);
    },
    {
      // Deferring until idle avoids flickering and is more performant
      deferUpdateUntilIdle: true,
      disableDimensionsUpdates: true,
    },
    [setIsScrolled],
  );

  const extraPadding = tiers ? 16 : 0;
  const headerBottomPadding = 8 + extraPadding;

  const couponLabel = t('Coupon');

  return (
    <>
      <Content
        data-testid={dataTestId}
        data-collapsed={isScrolled}
        $marginBottom={Math.ceil(
          bodyHeight + footerHeight + headerBottomPadding,
        )}
      >
        <InnerContent>
          <AnimationWrapper data-testid={`${dataTestId}couponWrapper`}>
            <Header>
              <GoBackIcon
                data-testId="couponBackButton"
                asset={GenericIconName.ArrowLeft}
                size="S"
                fill={COLORS.PrimaryCoupon}
                onClick={onBackClick}
              />
              <BadgesWrapper>
                {disableUrgencyTriggers ? (
                  <CouponHeadingLabel>{couponLabel}</CouponHeadingLabel>
                ) : (
                  expiryDate && (
                    <StyledCouponExpiryDateCountdown
                      expiryDate={expiryDate}
                      preventUnitComponentEqualWidth
                    />
                  )
                )}
                {isAppOnlyCoupon && <CouponAppOnlyBadge />}
              </BadgesWrapper>
              <InfoIcon
                data-testid={`${dataTestId}InfoIcon`}
                onClick={onInfoIconClick}
              >
                <InfoSVG
                  asset={GenericIconName.InfoWithoutFill}
                  size="XS"
                  fill={COLORS.PrimaryCoupon}
                />
              </InfoIcon>
              <CouponInfoContentPopper
                LayoutComponent={Flyout}
                couponInfo={info}
              />
            </Header>
            <Body>
              {discountValue && (
                <CouponBodyWithDiscountValue
                  ref={bodyRef}
                  discountValue={discountValue}
                  headline={headline}
                  dataTestId={dataTestId}
                  discountCondition={discountCondition}
                />
              )}
              {tiers && <CouponBodyWithTiers tiers={tiers} ref={bodyRef} />}
            </Body>
          </AnimationWrapper>

          <StyledCouponFooter
            ref={footerRef}
            brandAndCategory={brandAndCategory}
            productAndBrandInformation={productAndBrandInformation}
            data-testid={dataTestId}
            isOnlyAppCoupon={false}
            productsInformation={productsInformation}
          />
        </InnerContent>
      </Content>
    </>
  );
};

const StyledCouponTiers = styled(CouponTiers)<{$tiersNumber: number}>`
  font-size: ${FONT_SIZES.px28};
  transition-property: width;
  transition-duration: 0.4s;

  [data-collapsed='true'] & {
    font-size: ${FONT_SIZES.px22};
    width: 100%;

    @media ${mediaMinXXXS} {
      font-size: ${FONT_SIZES.px24};
    }
  }
`;

const DiscountValuePart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const TierWrapper = styled.div`
  width: 100%;
  margin: 8px 0;
  display: flex;
  justify-content: center;

  [data-collapsed='true'] & {
    padding-inline: 12px;
  }
`;

const DiscountValueWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  padding: 0 16px 12px;
  min-height: 60px;

  // eslint-disable-next-line local-rules/css-layout-animation
  transition: padding 300ms;

  [data-collapsed='true'] & {
    padding: 0px 23px;
  }
`;

const InnerContent = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 0 16px 0;
  color: inherit;
  justify-content: space-between;
  width: 100%;
  color: ${COLORS.PrimaryCoupon};
  position: absolute;
  top: 16px;
  z-index: 3;

  [data-collapsed='true'] & {
    z-index: 2;
  }
`;

const Body = styled.div`
  --body-padding-top: var(--content-height);
  --body-width: calc(100% - 32px);
  display: flex;
  align-items: center;
  color: inherit;
  overflow: hidden;
  color: ${COLORS.PrimaryCoupon};
  padding-top: var(--body-padding-top);
  // eslint-disable-next-line local-rules/css-layout-animation
  transition: padding-top, 0.3s, width 0.3s;
  z-index: 2;
  width: var(--body-width);

  [data-collapsed='true'] & {
    --body-width: 80%;
    --body-padding-top: 0px;
  }
`;

const AnimationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  border-top: 1px solid ${COLORS.BackgroundCoupon};
  border-bottom: 1px solid ${COLORS.BorderColorCoupon};
  background-color: ${COLORS.BackgroundCoupon};
  z-index: 1;
  position: relative;

  transition-duration: 0.3s;
  transition-property: transform;

  [data-collapsed='true'] & {
    border-top: 1px solid ${COLORS.BorderColorCoupon};
  }
`;

const InfoIcon = styled.div`
  overflow: hidden;
  cursor: pointer;
  z-index: 1;
`;

const InfoSVG = styled(Icon)`
  z-index: 1;
`;

const Headline = styled(Text)<{$isHeadlineMoreThanThreeLines?: boolean}>`
  line-height: 1.4;
  padding: 0 0 0 32px;

  ${({$isHeadlineMoreThanThreeLines}) => css`
    [data-collapsed='true'] & {
      line-height: 1.6;
      padding: 0 0 0 16px;

      ${$isHeadlineMoreThanThreeLines &&
      css`
        font-size: ${FONT_SIZES.px12};
      `};
    }
  `}
`;

const StyledCouponFooter = styled(CouponFooter)`
  border-bottom: 1px solid ${COLORS.BorderColorCoupon};
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.08);
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  width: 100%;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;

  transition-property: transform;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition-duration: 300ms;

  [data-collapsed='true'] & {
    transform: translateY(-120px);
  }
`;

const DiscountValue = styled(Text)`
  font-weight: 800;
  font-size: ${FONT_SIZES.px28};

  [data-collapsed='true'] & {
    font-size: ${FONT_SIZES.px24};
  }
`;

const DiscountConditionText = styled(Text)`
  white-space: nowrap;
`;

const Content = styled.div<{$marginBottom: number}>`
  --content-height: 60px;
  position: sticky;
  top: 50px;
  display: flex;
  flex-direction: column;
  z-index: 1;
  transition-property: transform;
  // we need to reduce height to what we expect
  height: var(--content-height);

  ${({$marginBottom}) => css`
    margin-bottom: ${$marginBottom}px;
  `}

  ::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    margin: -1px;
  }
`;

const StyledCouponExpiryDateCountdown = styled(CouponExpiryDateCountdown)`
  transition-property: opacity;
  // eslint-disable-next-line local-rules/css-layout-animation
  transition-duration: 200ms;

  && {
    padding: 4px 8px 4px 5px;
  }

  ${StyledCouponExpiryDateCountdownUnitComponent} {
    font-size: ${FONT_SIZES.px12};
    line-height: 1.16;
  }

  ${StyledCouponExpiryDateCountdownIcon}${StyledCouponExpiryDateCountdownIcon} {
    width: 16px;
    height: 16px;
    flex-basis: 16px;
  }
`;

const GoBackIcon = styled(Icon)`
  position: relative;
  left: -6px;
`;

const BadgesWrapper = styled.div`
  display: flex;
  gap: 4px;

  [data-collapsed='true'] & {
    opacity: 0;
  }
`;

const CouponHeadingLabel = styled.span`
  font-size: ${FONT_SIZES.px24};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
`;
